import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { Bell, X } from 'lucide-react';
import { isAuthenticated, getLicenseStatus } from '../services/auth';

const Pricing = () => {
  const { darkMode } = useContext(ThemeContext);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [hasNotified, setHasNotified] = useState(false);
  const [hideTrialBanner, setHideTrialBanner] = useState(isAuthenticated());
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  useEffect(() => {
    const checkLicenseStatus = async () => {
      if (isAuthenticated()) {
        try {
          const status = await getLicenseStatus();
          // Hide trial prompts if user has/had a trial or has a paid license
          setHideTrialBanner(status.trialLicense || status.hasPaidLicense);
          // Check if user has a paid license (active, trialing, or cancelled but not expired)
          setHasActiveSubscription(status.hasPaidLicense);
        } catch (error) {
          // If we can't check license status, show the trial prompts
          console.error('Error checking license status:', error);
          setHideTrialBanner(false);
        }
      } else {
        setHideTrialBanner(false);
      }
    };
    checkLicenseStatus();
  }, []);


  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-white dark:bg-gray-900`}>
      <div className="flex-grow">

        {/* Header */}
        <div className="text-center pt-16 pb-10 px-4">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white tracking-tight mb-4">
            One plan. <span className="text-gray-400 dark:text-gray-500">Everything included.</span>
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            No tiers, no feature gates. Every subscription gets full access to every feature.
          </p>
        </div>

        {/* Free Trial Banner */}
        {!hideTrialBanner && (
          <div className="max-w-3xl mx-auto mb-12 px-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-green-900 dark:text-green-300">Try free for 7 days</p>
                <p className="text-sm text-green-700 dark:text-green-400">Full access to all features. No credit card required.</p>
              </div>
              <Link
                to="/register"
                className="inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors whitespace-nowrap"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        )}

        {/* Pricing Card */}
        <div className="max-w-5xl mx-auto px-4 mb-16">
          <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-6 md:p-10">

            <div className="grid md:grid-cols-2 gap-10">

              {/* Left — Plans & CTA */}
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Stonewall Invoicing</h2>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2.5 py-1 rounded-lg font-semibold">Save up to 30%</span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">Full access to every feature. Pick your billing cycle.</p>

                <div className="space-y-2 mb-6">
                  <div
                    onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                    className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                  >
                    <span className="font-medium text-gray-900 dark:text-white">Monthly</span>
                    <span className="font-bold text-gray-900 dark:text-white">$119.99<span className="text-sm font-normal text-gray-400">/mo</span></span>
                  </div>

                  <div
                    onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                    className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">Quarterly</span>
                      <span className="text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-lg font-semibold">Save $29.98</span>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white">$109.99<span className="text-sm font-normal text-gray-400">/mo</span></span>
                  </div>

                  <div
                    onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                    className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">Biannual</span>
                      <span className="text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-lg font-semibold">Save $119.95</span>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white">$99.99<span className="text-sm font-normal text-gray-400">/mo</span></span>
                  </div>

                  <div
                    onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                    className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-700 border-2 border-blue-500 dark:border-blue-400 cursor-pointer hover:border-blue-600 dark:hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">Annual</span>
                      <span className="text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-lg font-semibold">Save $439.89</span>
                      <span className="text-[10px] bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-lg font-bold uppercase tracking-wide">Best value</span>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white">$999.99<span className="text-sm font-normal text-gray-400">/yr</span></span>
                  </div>
                </div>

                <Link
                  to={isAuthenticated() ? "/account/subscription" : "/register"}
                  className="w-full inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-lg text-base font-semibold transition-colors"
                >
                  {hasActiveSubscription ? 'Manage Subscription' : 'Get Started'}
                </Link>
                {!hideTrialBanner && (
                  <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-2">7-day free trial &middot; No credit card required</p>
                )}
              </div>

              {/* Right — What's included */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-5">What's included</h3>
                <ul className="space-y-3">
                  {[
                    'Unlimited invoices',
                    'Customer database with search',
                    'Complete vehicle database with FREE yearly updates',
                    'Reports with advanced filtering',
                    'Flexible discounts (per-item and invoice-level)',
                    'PDF generation and direct printing',
                    'Custom line item types and tax rates',
                    'Company branding and logo',
                    'Encrypted local database',
                    'Automatic app updates',
                  ].map((feature) => (
                    <li key={feature} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

            </div>
          </div>
        </div>

        {/* Cloud — Coming Soon */}
        <div className="max-w-5xl mx-auto px-4 mb-16">
          <div className="bg-gray-900 dark:bg-[#111] rounded-lg p-8 md:p-12 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div className="md:max-w-xl">
                <span className="text-xs uppercase tracking-widest text-blue-400 font-semibold">Coming Soon</span>
                <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-3 tracking-tight">Stonewall Cloud</h2>
                <p className="text-gray-400 mb-6">
                  Access your business data from anywhere. Multi-user support, real-time sync, automatic cloud backups, and cross-device compatibility.
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    'All Professional features',
                    'Unlimited users',
                    'Cloud data sync',
                    'Real-time collaboration',
                    'Cross-device access',
                    'Automatic backups',
                  ].map((item) => (
                    <div key={item} className="flex items-center">
                      <svg className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0">
                <button
                  className={`px-8 py-3.5 rounded-lg text-base font-semibold transition-colors ${
                    hasNotified
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-white text-gray-900 hover:bg-gray-100'
                  }`}
                  onClick={() => setShowNotifyModal(true)}
                >
                  {hasNotified ? 'Notified!' : 'Notify Me'}
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
      
      {/* Notify Me Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 relative transform transition-all">
            <button
              onClick={() => setShowNotifyModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Get Notified About Stonewall Cloud</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Be the first to know when our cloud solution launches. Enter your email below and we'll keep you updated!
              </p>
            </div>
            
            {!hasNotified ? (
              <form onSubmit={(e) => {
                e.preventDefault();
                setHasNotified(true);
              }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowNotifyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Me
                </button>
              </div>
            </form>
            ) : (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Thank You!</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    We've added {notifyEmail} to our notification list. You'll be the first to know when Stonewall Cloud launches!
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowNotifyModal(false);
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;